import type { Vector3Json } from 'contracts/basic';
import type {
  EdgeJson,
  FaceJson,
  RefJson,
  TessJson,
  TextJson,
  VertexJson,
} from 'contracts/tess';
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Sphere,
  Uint16BufferAttribute,
  Vector3,
} from 'three';

import { SimpleProgressController } from '@frontend/controllers';
import type { ProgressSignal } from '@frontend/controllers';
import { Stack } from '@frontend/datatypes';
import {
  WaitUntilSetController,
  wait,
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import { getOrCreateThreeColor } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import { ENTITY_TYPES, PART_TYPES } from '../../constants';
// eslint-disable-next-line no-restricted-imports
import {
  crossToTarget,
  dot,
  normalizeToTarget,
  scaleVecToTarget,
  vecAddToTarget,
  vecSubToTarget,
} from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../../renderer/loadingPriorityQueue';

const faceSplitCount = 200;
const edgeSplitCount = 300;

type BufferRange = {
  bufNum: number; //face BufferGeometry's index in array
  bufStart: number; //index in BufferGeometry that this face's tesselation starts
  bufCount: number; //number of positions this face has in BufferGeometry (bufCount ==== tessCount * 3)
} & BoundBoxJson &
  BoundSphereJson;

export type FaceMetaData = FaceJson['meta'] & {
  index: number; //ind from the original FaceJson
  bufferRanges: BufferRange[];
  color?: number; //color as hex number to save space
} & BoundBoxJson &
  BoundSphereJson;

export type EdgeMetaData = EdgeJson['meta'] & {
  index: number; //ind from the original EdgeJson
  bufferRanges: BufferRange[];
} & BoundBoxJson &
  BoundSphereJson;

export type MetaDataType = Exclude<ENTITY_TYPES, ENTITY_TYPES.STL>;
export type VertexMetaData = Required<VertexJson>['meta'] & { index: number };
export type TextMetaData = Required<TextJson>['meta'] & { index: number };
export type RefMetaData = Required<RefJson>['meta'] & { index: number };

export type VertexData = VertexJson & { meta: VertexMetaData };
export type TextData = TextJson & { meta: TextMetaData };
//export type RefData = RefJson & { meta: RefMetaData };

export type GeometryData = {
  faceGeometries?: FaceGeometry[];
  faceMetaData?: FaceMetaData[];
  edgeGeometries?: EdgeGeometry[];
  edgeMetaData?: EdgeMetaData[];
  vertices?: VertexData[];
  texts?: TextData[];
  refs?: RefMetaData[];
};

//65535 is size of Uint16Array less one.
const maxFacePositions = Math.floor(65535 / 9) * 9; // = 65529 (positions must be multiple of 9 (triangle has 3 vertices and 3 numbers per vertex))
const maxEdgePositions = Math.floor(65535 / 6) * 6; //= 65532 (positions be divisible by 6 (edge segment has 2 vertices and 3 numbers per vertex)
//const maxEdgeIndices = ((maxEdgePositions / 3) - 1) * 2; // = 43686 (Must be less than 65535) (2 indices for every edge segment)

const vector3ToJson = ({ x, y, z }: Vector3): Vector3Json => [x, y, z];

export const sphereToSphereJson = ({
  center,
  radius,
}: Sphere): BoundSphereJson['boundSphere'] => ({
  center: vector3ToJson(center),
  radius,
});

export const boxToBoxJson = ({ min, max }: Box3): BoundBoxJson['boundBox'] => ({
  min: vector3ToJson(min),
  max: vector3ToJson(max),
});

type BoundBoxJson = { boundBox: { min: Vector3Json; max: Vector3Json } };

type BoundSphereJson = { boundSphere: { center: Vector3Json; radius: number } };

type BufferRangeJson = Omit<BufferRange, 'boundBox' | 'boundSphere'> &
  BoundBoxJson &
  BoundSphereJson;

type BufferRangesJson = {
  bufferRanges: BufferRangeJson[];
};

//CloneableEdgeMetaData: Don't include boundBox and boundSphere (it can be calculated when loading)
//Could consider storing it if calculating on load is a performance issue. By excluding, indexedDB usage is a bit less and in future
//if we receive CloneableSTEPGeometry via protobuf, it will be a smaller network payload, and it means the backend doesn't need to calculate this
type CloneableEdgeMetaData = Omit<
  EdgeMetaData,
  'boundBox' | 'boundSphere' | 'bufferRanges'
> &
  BoundBoxJson &
  BoundSphereJson &
  BufferRangesJson;

type CloneableFaceMetaData = Omit<
  FaceMetaData,
  'boundBox' | 'boundSphere' | 'bufferRanges'
> &
  BoundBoxJson &
  BoundSphereJson &
  BufferRangesJson;

export type CloneableSTEPGeometry = {
  faceMetaData: CloneableFaceMetaData[];
  //faceBuffers is the data used to create the BufferGeometries
  faceBuffers: {
    positions: Float32Array;
    normals?: Float32Array;
    indices?: Uint16Array;
    uvs?: Float32Array;
  }[];
  edgeMetaData: CloneableEdgeMetaData[];
  //edgeBuffers is the data used to create the BufferGeometries
  edgeBuffers: {
    positions: Float32Array;
    indices?: Uint16Array;
  }[];
  vertices: VertexData[];
  texts?: TextData[];
  refs?: RefMetaData[];
  type: PART_TYPES.STEP | PART_TYPES.DXF;
};

type FaceGeometry = {
  positions: Float32BufferAttribute;
  normals?: Float32BufferAttribute;
  indices?: Uint16BufferAttribute;
  uvs?: Float32BufferAttribute;
};

type EdgeGeometry = {
  positions: Float32BufferAttribute;
  indices?: Uint16BufferAttribute;
};

type FaceParseState = {
  positions: number[]; //used to collect positions for FaceGeometry
  normals: number[]; //used to collect normals for FaceGeometry
  indices: number[]; //used to collect indices for FaceGeometry
  //Cache cb, ab, ca, normal vectors that can be reused for calculations and eliminate need for
  //repeated new objects and garbage collection
  cb: Vector3Json;
  ab: Vector3Json;
  ca: Vector3Json;
  normal: Vector3Json;
};

type EdgeParseState = {
  positions: number[];
  indices: number[];
  j: number; //index in positions for next indice
};

export class STEPGeometry implements AsyncLoadable, SyncLoadable {
  public static createProgress(
    data: Pick<TessJson, 'Faces' | 'Edges'>,
    id: string,
    name: string,
  ): SimpleProgressController | undefined {
    const todoCount = (data.Faces || []).length + (data.Edges || []).length;

    if (todoCount) {
      return new SimpleProgressController({
        id,
        description: `Loading Geometry for Part ${name || id}`,
        todoCount,
        expectedMs: Math.ceil(0.1 * todoCount),
        start: false,
      });
    }
  }

  public _vertices?: VertexData[];
  public _texts?: TextData[];

  private faceJsons?: FaceJson[]; //only defined when loading
  private edgeJsons?: EdgeJson[]; //only defined when loading

  //The face geometries are consoldiated inside BufferGeometry. As many as `maxFacePositions` (size of Uint16Array) edges can be packed in each BufferGeometry
  //It is desired to pack multiple faces into a single BufferGeometry to reduce WebGL draw calls.
  public _faceGeometries: FaceGeometry[] = [];
  private _faceMetaData: FaceMetaData[];

  //The edge geometries are consoldiated inside BufferGeometry. As many as `maxIndices` (size of Uint16Array) edges can be packed in each BufferGeometry
  //It is desired to pack multiple edges into a single BufferGeometry to reduce WebGL draw calls.
  public _edgeGeometries: EdgeGeometry[] = [];
  private _edgeMetaData: EdgeMetaData[];

  private _refs?: RefMetaData[];

  private colorLookup?: Map<number, number>;
  private resumeController?: WaitUntilSetController<void>;
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  public readonly type: PART_TYPES.STEP | PART_TYPES.DXF;
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: TessJson,
    private _progress?: SimpleProgressController, //create progress in advance so it can be composed and composedProgress closed. Use STEPGeometry.createProgress()
    private readonly signal?: AbortSignal, //AbortSignal to abort loading
    {
      faceGeometries = [],
      faceMetaData = [],
      edgeGeometries = [],
      edgeMetaData = [],
      vertices = [],
      texts = [],
      refs = [],
    }: GeometryData = {},
  ) {
    this.type = data.Type ?? PART_TYPES.STEP;
    this._faceGeometries = faceGeometries;
    this._faceMetaData = faceMetaData;
    this._edgeGeometries = edgeGeometries;
    this._edgeMetaData = edgeMetaData;
    if (refs.length) {
      this._refs = refs;
    }
    if (
      (data.Vertexes && data.Vertexes.length > 0) ||
      (vertices && vertices.length > 0)
    ) {
      this._vertices = [
        ...data.Vertexes.map((v) => ({
          ...v,
          meta: { ...v.meta, index: v.ind },
        })),
        ...vertices.map((v) => ({ ...v, meta: { ...v.meta, index: v.ind } })),
      ];
    }
    if ((data.Texts && data.Texts.length > 0) || (texts && texts.length > 0)) {
      this._texts = [
        ...(data.Texts ?? []).map((t) => ({
          ...t,
          meta: { ...t.meta, index: t.ind },
        })),
        ...texts.map((t) => ({ ...t, meta: { ...t.meta, index: t.ind } })),
      ];
    }
    if ((data.Refs && data.Refs.length > 0) || (refs && refs.length > 0)) {
      this._refs = [
        ...(data.Refs ?? []).map(
          (r) => ({ ...r.meta, index: r.ind } as RefMetaData),
        ),
        ...refs,
      ];
    }

    this.faceJsons = data.Faces;
    this.edgeJsons = data.Edges;
    if (data.Colors) {
      //Create temporary inverse map: color by face index
      //Note: Some indices may not be included in data.Colors.
      //(If so, the StepFaceBody instance's .baseColor is used)
      this.colorLookup = new Map();
      for (const [colorString, list] of Object.entries(data.Colors)) {
        //Provided colorString also includes alpha. It is removed today.
        const color = getOrCreateThreeColor(
          colorString.substring(0, 7),
        ).getHex();
        for (const startIndexOrRange of list) {
          if (typeof startIndexOrRange === 'number') {
            this.colorLookup.set(startIndexOrRange, color);
          } else if (Array.isArray(startIndexOrRange)) {
            const [startIndex, count] = startIndexOrRange as [number, number];
            for (let i = 0; i < count; i++) {
              this.colorLookup.set(startIndex + i, color);
            }
          }
        }
      }
    }
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public static fromJSON(
    data: TessJson,
    id: string,
    name: string,
    signal?: AbortSignal, //AbortSignal to abort loading
  ): [STEPGeometry, ProgressSignal<SimpleProgressController>?] {
    const progress = STEPGeometry.createProgress(data, id, name);
    return [new STEPGeometry(data, progress, signal), progress?.signal];
  }

  public static createGeometryData({
    faceBuffers,
    faceMetaData,
    edgeBuffers,
    edgeMetaData,
    vertices,
    texts,
    refs,
  }: CloneableSTEPGeometry): Required<GeometryData> {
    return {
      faceGeometries: faceBuffers.map<FaceGeometry>((face) => ({
        //Note: First argument of BufferAttribute must be typed array. (Typescript doesn't specify this but src does)
        //Don't use new Float32BufferAttribute(), new Uint16BufferAttribute() etc because the input array is already typed
        positions: new BufferAttribute(face.positions, 3),
        ...(face.normals && {
          normals: new BufferAttribute(face.normals, 3),
        }),
        ...(face.indices && {
          indices: new BufferAttribute(face.indices, 1),
        }),
        ...(face.uvs && { uvs: new BufferAttribute(face.uvs, 2) }),
      })),
      faceMetaData,
      edgeGeometries: edgeBuffers.map<EdgeGeometry>((edge) => ({
        //Note: First argument of BufferAttribute must be typed array. (Typescript doesn't specify this but src does)
        //Don't use new Float32BufferAttribute(), new Uint16BufferAttribute() etc because the input array is already typed
        positions: new BufferAttribute(edge.positions, 3),
        ...(edge.indices && {
          indices: new BufferAttribute(edge.indices, 1),
        }),
      })),
      edgeMetaData,
      vertices: vertices ?? [],
      texts: texts ?? [],
      refs: refs ?? [],
    };
  }

  public static fromCloneable(data: CloneableSTEPGeometry): STEPGeometry {
    const geometry = new STEPGeometry(
      {
        Faces: [],
        Edges: [],
        Vertexes: [],
        Texts: [],
        Refs: [],
        Type: data.type,
      },
      undefined, //no progress
      undefined, //no signal
      STEPGeometry.createGeometryData(data),
    );
    delete geometry.faceJsons;
    delete geometry.edgeJsons;
    return geometry;
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    for (const a of [
      this._vertices,
      this._texts,
      this.faceJsons,
      this.edgeJsons,
      this._faceGeometries,
      this._faceMetaData,
      this._edgeGeometries,
      this._edgeMetaData,
      this._refs,
    ]) {
      if (a) a.length = 0;
    }
    if (this.colorLookup) this.colorLookup.clear();
  }

  public getEntityMeta(
    index: number,
    type: MetaDataType,
  ):
    | FaceMetaData
    | EdgeMetaData
    | VertexMetaData
    | TextMetaData
    | RefMetaData
    | undefined {
    switch (type) {
      case ENTITY_TYPES.FACE:
        //eslint-disable-next-line no-case-declarations
        const faceCandidate = this._faceMetaData[index];
        return faceCandidate && faceCandidate.index === index
          ? faceCandidate
          : this._faceMetaData?.find((f) => f.index === index);
      case ENTITY_TYPES.EDGE:
        //eslint-disable-next-line no-case-declarations
        const edgeCandidate = this._edgeMetaData[index];
        return edgeCandidate && edgeCandidate.index === index
          ? edgeCandidate
          : this._edgeMetaData?.find((e) => e.index === index);
      case ENTITY_TYPES.VERTEX:
        //eslint-disable-next-line no-case-declarations
        const vertexCandidate = this._vertices?.[index]?.meta;
        return vertexCandidate && vertexCandidate.index === index
          ? vertexCandidate
          : this._vertices?.find((v) => v.meta.index === index)?.meta;
      case ENTITY_TYPES.TEXT:
        //eslint-disable-next-line no-case-declarations
        const textCandidate = this._texts?.[index]?.meta;
        return textCandidate && textCandidate.index === index
          ? textCandidate
          : this._texts?.find((t) => t.meta.index === index)?.meta;
      case ENTITY_TYPES.REF:
        //eslint-disable-next-line no-case-declarations
        const refCandidate = this._refs?.[index];
        return refCandidate && refCandidate.index === index
          ? refCandidate
          : this._refs?.find((r) => r.index === index);
    }
  }

  public refs(): IterableIterator<RefMetaData> {
    return (this._refs ?? []).values();
  }

  public getRef(index: number): RefMetaData | undefined {
    const refCandidate = this._refs?.[index];
    return refCandidate && refCandidate.index === index
      ? refCandidate
      : this._refs?.find((r) => r.index === index);
  }

  public *getEntitiesForRef(
    ref: RefMetaData,
  ): IterableIterator<
    [MetaDataType, FaceMetaData | EdgeMetaData | TextMetaData | VertexMetaData]
  > {
    let rootRef: RefMetaData = ref;

    //Walk up tree of refs until the root is found
    while (rootRef.parentIndex) {
      const candidate = this.getEntityMeta(
        rootRef.parentIndex,
        ENTITY_TYPES.REF,
      ) as RefMetaData | undefined;
      if (candidate) {
        rootRef = candidate;
      } else {
        break;
      }
    }

    //Iterate over descendents of rootRef and yield those members
    const stack = Stack.of<[Exclude<ENTITY_TYPES, ENTITY_TYPES.STL>, number]>(
      ...(rootRef.members ?? []),
    );
    let item: [Exclude<ENTITY_TYPES, ENTITY_TYPES.STL>, number] | undefined;
    //eslint-disable-next-line no-cond-assign
    while ((item = stack.pop())) {
      const [type, index] = item;
      const childMember = this.getEntityMeta(index, type);
      if (childMember) {
        if (type === ENTITY_TYPES.REF) {
          for (const nextChildMember of (childMember as RefMetaData).members ??
            []) {
            stack.push(nextChildMember);
          }
        } else {
          yield [type, childMember];
        }
      }
    }
  }

  public *getSelfAndDecendentRefs(
    rootRef: RefMetaData,
  ): IterableIterator<RefMetaData> {
    const stack = Stack.of<RefMetaData>(rootRef);
    yield rootRef;
    let ref: RefMetaData | undefined;
    //eslint-disable-next-line no-cond-assign
    while ((ref = stack.pop())) {
      if (ref) {
        for (const [type, index] of ref.members ?? []) {
          if (type === ENTITY_TYPES.REF) {
            const childRef = this.getEntityMeta(index, type) as RefMetaData;
            if (childRef) {
              stack.push(childRef);
              yield childRef;
            }
          }
        }
      }
    }
  }

  public get faceMetaData(): FaceMetaData[] {
    return this._faceMetaData;
  }

  public get faceGeometries(): FaceGeometry[] {
    return this._faceGeometries;
  }

  public get faceCount(): number {
    return this._faceMetaData.length + (this.faceJsons?.length ?? 0);
  }

  public get faceVerticesCount(): number {
    return (
      this._faceGeometries.reduce(
        (count, g) => count + g.positions.array.length,
        0,
      ) / 3
    );
  }

  public get edgeMetaData(): EdgeMetaData[] {
    return this._edgeMetaData;
  }

  public get edgeGeometries(): EdgeGeometry[] {
    return this._edgeGeometries;
  }

  public get edgeCount(): number {
    return this._edgeMetaData.length + (this.edgeJsons?.length ?? 0);
  }

  public get edgeVerticesCount(): number {
    return (
      this._edgeGeometries.reduce(
        (count, g) => count + g.positions.array.length,
        0,
      ) / 3
    );
  }

  //geometryVerticesCount = edgeVerticesCount + faceVerticesCount;
  public get geometryVerticesCount(): number {
    let count = 0;
    for (const g of this._edgeGeometries) {
      count += g.positions.array.length;
    }
    for (const g of this._faceGeometries) {
      count += g.positions.array.length;
    }
    return count / 3;
  }

  public get refCount(): number {
    return this._refs?.length ?? 0;
  }

  public get vertices(): VertexData[] | undefined {
    return this._vertices;
  }

  public get texts(): TextData[] | undefined {
    return this._texts;
  }

  public get progress(): ProgressSignal<SimpleProgressController> | undefined {
    return this._progress?.signal;
  }

  public get isLoaded(): boolean {
    return !this.faceJsons && !this.edgeJsons;
  }

  public get isLoading(): boolean {
    return !!this.loaded;
  }

  public pause() {
    if (!this.isLoaded && !this.resumeController) {
      this.resumeController = new WaitUntilSetController(this.signal);
    }
  }

  public get isPaused(): boolean {
    return !!this.resumeController;
  }

  public resume() {
    if (this.resumeController) {
      this.resumeController.set();
      delete this.resumeController;
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    if (this.resumeController) {
      this.resumeController.setReject();
      delete this.resumeController;
    }
    if (this._progress) {
      this._progress.done();
    }
  }

  //load(): Alternative to this.loadAsync()
  //* Not preferred for geometry with many faces and edges because this is a sync method and it will block UI.
  //* Also note there is static STEP.Geometry.fromCloneable() which is synchronous and faster because its not based on JSON
  public load(): void {
    if (this.isLoaded || this.signal?.aborted) {
      return;
    } else if (this.loaded) {
      //isLoading is true because this.loaded promise exists
      throw new Error(
        'Cannot load STEPGeometry synchronously that already started async loading',
      );
    } else {
      //If Not loaded, and not isLoading (not loading async), then load synchronously
      //* Warning: loading synchronously may block UI
      try {
        this._progress?.start();

        const faceParseState: FaceParseState = {
          positions: [],
          normals: [],
          indices: [],
          cb: [0, 0, 0],
          ab: [0, 0, 0],
          ca: [0, 0, 0],
          normal: [0, 0, 0],
        };

        const edgeParseState: EdgeParseState = {
          positions: [],
          indices: [],
          j: -1,
        };

        for (const faceJson of this.faceJsons ?? []) {
          this.loadFace(faceJson, faceParseState);
        }
        for (const edgeJson of this.edgeJsons ?? []) {
          this.loadEdge(edgeJson, edgeParseState);
        }

        //Finalize and push last FaceGeometry and last EdgeGeometry
        this.addFaceParseState(faceParseState);
        this.addEdgeParseState(edgeParseState);
      } finally {
        if (this._progress) {
          this._progress.done();
          delete this._progress;
        }
        if (this.faceJsons) {
          this.faceJsons.length = 0;
        }
        if (this.edgeJsons) {
          this.edgeJsons.length = 0;
        }
        delete this.faceJsons;
        delete this.edgeJsons;
        this.resume(); //cleanup resumeController if it exists
      }
    }
  }

  //loadAync(): Preferred way to load geometry. With loadAsync:
  //* loading can be paused/resumed with this.pause() and this.resume() respectively
  //* loading runs in 50ms chunks so the UI is not blocked
  public loadAsync(lastPause: number = performance.now()): Promise<number> {
    if (this.isLoaded || this.signal?.aborted) {
      return Promise.resolve(lastPause);
    }
    if (!this.loaded) {
      //Not loaded, and not isLoading
      this.loaded = (async (): Promise<number> => {
        try {
          this._progress?.start();

          const faceParseState: FaceParseState = {
            positions: [],
            normals: [],
            indices: [],
            cb: [0, 0, 0],
            ab: [0, 0, 0],
            ca: [0, 0, 0],
            normal: [0, 0, 0],
          };

          const edgeParseState: EdgeParseState = {
            positions: [],
            indices: [],
            j: -1,
          };

          if (this.faceJsons) {
            for (const [index, faceJson] of this.faceJsons.entries()) {
              //Every faceSplitCount faces, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
              //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
              //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
              //Can't use macro because they do not exist in js/ts yet.
              if (index % faceSplitCount === 0) {
                if (this.resumeController) {
                  await this.resumeController.waitUntilSet;
                  //eslint-disable-next-line no-param-reassign
                  lastPause = performance.now();
                }
                const now = performance.now();
                if (now - lastPause > 50) {
                  await wait();
                  //eslint-disable-next-line no-param-reassign
                  lastPause = now;
                }
                if (this.signal?.aborted) {
                  break;
                }
                this._progress?.addProgress(faceSplitCount);
              }
              this.loadFace(faceJson, faceParseState);
            }
          }

          if (this.edgeJsons) {
            for (const [index, edgeJson] of this.edgeJsons.entries()) {
              //Every edgeSplitCount edges, consider pausing (if paused) or deferring after 50ms of continuous work so we don't block UI
              //Note: It would be nice to make this if block a macro or fn. I am not using fn for performance reasons
              //because the fn would have to be async and would always defer each loop. But when inlined here it is only defer'ing when needed
              //Can't use macro because they do not exist in js/ts yet.
              if (index % edgeSplitCount === 0) {
                //if paused, wait till it resumes
                if (this.resumeController) {
                  await this.resumeController.waitUntilSet;
                  //eslint-disable-next-line no-param-reassign
                  lastPause = performance.now();
                }
                //defer after 50ms of work so that UI is not blocked
                const now = performance.now();
                if (now - lastPause > 50) {
                  await wait();
                  //eslint-disable-next-line no-param-reassign
                  lastPause = now;
                }
                if (this.signal?.aborted) {
                  break;
                }
                this._progress?.addProgress(edgeSplitCount);
              }

              this.loadEdge(edgeJson, edgeParseState);
            }
          }

          //Finalize and push last FaceGeometry and last EdgeGeometry
          this.addFaceParseState(faceParseState);
          this.addEdgeParseState(edgeParseState);
        } finally {
          if (this.faceJsons) {
            this.faceJsons.length = 0;
          }
          if (this.edgeJsons) {
            this.edgeJsons.length = 0;
          }
          if (this.edgeJsons) {
            this.edgeJsons.length = 0;
          }
          delete this.loaded;
          if (this._progress) {
            this._progress.done();
            delete this._progress;
          }
          delete this.faceJsons;
          delete this.edgeJsons;
          this.resume(); //cleanup resumeController if it exists
        }
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded; //loadedPromise
  }

  public toCloneable(): CloneableSTEPGeometry {
    if (!this.isLoaded) {
      throw new Error('Cannot convert to cloneable if not loaded yet.');
    }
    const { _faceMetaData, _edgeMetaData } = this;
    return {
      faceMetaData: _faceMetaData,
      faceBuffers: this._faceGeometries.map<
        CloneableSTEPGeometry['faceBuffers'][number]
      >((faceGeometry) => ({
        positions: faceGeometry.positions?.array as Float32Array,
        ...(faceGeometry.normals?.array && {
          normals: faceGeometry.normals?.array as Float32Array,
        }),
        ...(faceGeometry.indices?.array && {
          indices: faceGeometry.indices?.array as Uint16Array,
        }),
        ...(faceGeometry.uvs?.array && {
          uvs: faceGeometry.uvs?.array as Float32Array,
        }),
      })),
      edgeMetaData: _edgeMetaData,
      edgeBuffers: this._edgeGeometries.map<
        CloneableSTEPGeometry['edgeBuffers'][number]
      >((edgeGeometry) => ({
        positions: edgeGeometry.positions?.array as Float32Array,
        ...(edgeGeometry.indices?.array && {
          indices: edgeGeometry.indices?.array as Uint16Array,
        }),
      })),
      vertices: this._vertices || [],
      ...(this._texts && this._texts.length && { texts: this._texts }),
      type: this.type,
    };
  }

  private loadFace(face: FaceJson, faceParseState: FaceParseState): void {
    const { connectivity, vertices, meta, ind } = face;
    if (
      !connectivity ||
      !connectivity.length ||
      !vertices ||
      !vertices.length
    ) {
      return;
    }
    const { positions, normals, cb, ab, ca, normal } = faceParseState;

    const bufferRanges: BufferRange[] = [];
    const boundBox = new Box3();
    const boundSphere = new Sphere();
    let bufStart = positions.length;

    const weightedNormals: Map<string, Vector3Json> = new Map();

    //Collect positions and normals
    //* Note: This is an expensive loop. If possible use fromCloneable() to save time.
    for (const [indexA, indexB, indexC] of connectivity) {
      const a = vertices[indexA];
      const b = vertices[indexB];
      const c = vertices[indexC];
      positions.push(...a, ...b, ...c);
      //eslint-disable-next-line no-param-reassign

      //Compute normals for each face's vertex (because normals were not provided)
      //--------------------------------------------------------------------------
      //See Appendix E: "Finding Normals from Polygon Data" page 592 (2nd last paragraph) of OpenGL Programming Guide (2nd edition)
      //for inspriation around weighting the normals by the size of the angles at the shared intersection.

      //Edge vectors to calculate normals:
      //When calculating weights, these are normalized now to simplify calculating angles ABC, BCA, and CAB.
      normalizeToTarget(vecSubToTarget(c, b, cb), cb);
      normalizeToTarget(vecSubToTarget(a, b, ab), ab);
      normalizeToTarget(vecSubToTarget(c, a, ca), ca);

      //Calculate weights for each normal to be saved in weightedNormalsByVertexKey. These will be aggregated when the weight normals for each vertex are accumulated
      //Weight is the angle of the triangle at the vertex.
      //Note: Sometimes there are rounding errors where the value passed to acos is slightly > 1 (ie 1.000000002). So rounding down to 1.0
      const ABC = Math.acos(Math.min(dot(cb, ab), 1.0));
      const BCA = Math.acos(Math.min(dot(cb, ca), 1.0));
      //const CAB = Math.acos(Math.min(-1 * dot(ca, ab), 1.0)); //Need vector BA (not AB) so multiply by -1
      const CAB = Math.PI - ABC - BCA;

      crossToTarget(cb, ab, normal);

      //Note: target for scaleVecToTarget could be any free Vector3Json. Using cb for each case
      const keyA = JSON.stringify(a);
      const keyB = JSON.stringify(b);
      const keyC = JSON.stringify(c);
      let normalA = weightedNormals.get(keyA);
      if (!normalA) {
        normalA = [0, 0, 0];
        weightedNormals.set(keyA, normalA);
      }
      let normalB = weightedNormals.get(keyB);
      if (!normalB) {
        normalB = [0, 0, 0];
        weightedNormals.set(keyB, normalB);
      }
      let normalC = weightedNormals.get(keyC);
      if (!normalC) {
        normalC = [0, 0, 0];
        weightedNormals.set(keyC, normalC);
      }
      vecAddToTarget(normalA, scaleVecToTarget(CAB, normal, cb), normalA);
      vecAddToTarget(normalB, scaleVecToTarget(ABC, normal, cb), normalB);
      vecAddToTarget(normalC, scaleVecToTarget(BCA, normal, cb), normalC);
    }

    for (const [indexA, indexB, indexC] of connectivity) {
      //finally normalize the weighted accumulations (so we have the mean average normal at each vertex)
      normals.push(
        ...normalizeToTarget(
          weightedNormals.get(JSON.stringify(vertices[indexA])) as Vector3Json,
          cb,
        ),
      );
      normals.push(
        ...normalizeToTarget(
          weightedNormals.get(JSON.stringify(vertices[indexB])) as Vector3Json,
          cb,
        ),
      );
      normals.push(
        ...normalizeToTarget(
          weightedNormals.get(JSON.stringify(vertices[indexC])) as Vector3Json,
          cb,
        ),
      );
    }

    const color = this.colorLookup?.get(ind);
    this.colorLookup?.delete(ind);

    while (positions.length > bufStart) {
      const bufCount = Math.min(positions.length, maxFacePositions) - bufStart;
      //accumulate the boundingBox and boundingSphere
      const tempFaceGeometry = new BufferGeometry().setAttribute(
        'position',
        new Float32BufferAttribute(
          positions.slice(bufStart, bufStart + bufCount),
          3,
        ),
      );
      tempFaceGeometry.computeBoundingBox(); //so that tempFaceGeometry.boundingBox is defined
      tempFaceGeometry.computeBoundingSphere(); //so that tempFaceGeometry.boundingSphere is defined
      boundBox.union(tempFaceGeometry.boundingBox as Box3);
      boundSphere.union(tempFaceGeometry.boundingSphere as Sphere);

      //accumulate bufferRanges for this face
      bufferRanges.push({
        bufStart,
        bufNum: this._faceGeometries.length,
        bufCount,
        boundSphere: sphereToSphereJson(
          tempFaceGeometry.boundingSphere as Sphere,
        ),
        boundBox: boxToBoxJson(tempFaceGeometry.boundingBox as Box3),
      });

      if (positions.length > maxFacePositions) {
        //accumulate attributes for a new buffer (reducing positions & normals and resetting bufStart)
        this._faceGeometries.push({
          positions: new Float32BufferAttribute(
            positions.splice(0, maxFacePositions),
            3,
          ),
          normals: new Float32BufferAttribute(
            normals.splice(0, maxFacePositions),
            3,
          ),
        });
        bufStart = 0;
      } else {
        break; //done extracting bufferRanges
      }
    }

    if (boundSphere.radius) {
      this._faceMetaData.push({
        ...meta,
        index: ind,
        bufferRanges,
        boundSphere: sphereToSphereJson(boundSphere),
        boundBox: boxToBoxJson(boundBox),
        ...(typeof color === 'number' && { color }), //if no color, the body's baseColor is used
      });
    }
    //Otherwise filter out faces that have no bounding sphere (they aren't real)
    //The faces persist in positions & normals... One day, I'd like to remove them
    //but it doesn't hurt to leave them.  The value of removing them from _faceMetaData
    //is that it saves creating unnecessary StepFaceModel instances.
    //Backend shouldn't send faces that don't occupy space.
  }

  private loadEdge(edge: EdgeJson, edgeParseState: EdgeParseState): void {
    const { points, meta, ind } = edge;
    if (!points || !points.length) {
      return;
    }

    const { positions, indices } = edgeParseState;

    const bufferRanges: BufferRange[] = [];
    const boundBox = new Box3();
    const boundSphere = new Sphere();
    let bufStart = positions.length;

    const pointValues = points.values();
    const [firstPoint] = pointValues;
    positions.push(...firstPoint);
    edgeParseState.j++;
    for (const point of pointValues) {
      positions.push(...point);
      indices.push(edgeParseState.j, ++edgeParseState.j);
      if (positions.length >= maxEdgePositions) {
        const bufCount = positions.length - bufStart;

        //accumulate the boundingBox and boundingSphere
        const tempEdgeGeometry = new BufferGeometry().setAttribute(
          'position',
          new Float32BufferAttribute(
            positions.slice(bufStart, bufStart + bufCount),
            3,
          ),
        );
        tempEdgeGeometry.computeBoundingBox(); //so that tempEdgeGeometry.boundingBox is defined
        tempEdgeGeometry.computeBoundingSphere(); //so that tempEdgeGeometry.boundingSphere is defined
        boundBox.union(tempEdgeGeometry.boundingBox as Box3);
        boundSphere.union(tempEdgeGeometry.boundingSphere as Sphere);

        //accumulate bufferRanges for this edge
        bufferRanges.push({
          bufStart,
          bufNum: this._edgeGeometries.length,
          bufCount,
          boundSphere: sphereToSphereJson(
            tempEdgeGeometry.boundingSphere as Sphere,
          ),
          boundBox: boxToBoxJson(tempEdgeGeometry.boundingBox as Box3),
        });
        this.addEdgeParseState(edgeParseState, true);
        bufStart = 0;
      }
    }
    if (positions.length > bufStart) {
      const bufCount = positions.length - bufStart;

      //accumulate the boundingBox and boundingSphere
      const tempEdgeGeometry = new BufferGeometry().setAttribute(
        'position',
        new Float32BufferAttribute(
          positions.slice(bufStart, bufStart + bufCount),
          3,
        ),
      );
      tempEdgeGeometry.computeBoundingBox(); //so that tempEdgeGeometry.boundingBox is defined
      tempEdgeGeometry.computeBoundingSphere(); //so that tempEdgeGeometry.boundingSphere is defined
      boundBox.union(tempEdgeGeometry.boundingBox as Box3);
      boundSphere.union(tempEdgeGeometry.boundingSphere as Sphere);

      //accumulate bufferRanges for this edge
      bufferRanges.push({
        bufStart,
        bufNum: this._edgeGeometries.length,
        bufCount,
        boundSphere: sphereToSphereJson(
          tempEdgeGeometry.boundingSphere as Sphere,
        ),
        boundBox: boxToBoxJson(tempEdgeGeometry.boundingBox as Box3),
      });
    }

    if (boundSphere.radius) {
      this._edgeMetaData.push({
        ...meta,
        index: ind,
        bufferRanges,
        boundSphere: sphereToSphereJson(boundSphere),
        boundBox: boxToBoxJson(boundBox),
      });
    }
    //Otherwise filter out edges that have no bounding sphere (they aren't real)
    //The edges persist in positions & indices... One day, I'd like to remove them
    //but it doesn't hurt to leave them.  The value of removing them from _edgeMetaData
    //is that it saves creating unnecessary StepEdgeModel instances
    //Backend shouldn't send edges that don't occupy space.
  }

  private addFaceParseState({
    positions,
    normals,
    indices,
  }: FaceParseState): void {
    if (positions.length) {
      //Finalize the faceGeometry and push it (and start preping next faceGeometry (positions/normals/indices zeroed))
      this._faceGeometries.push({
        positions: new Float32BufferAttribute(positions, 3),
        ...(normals?.length && {
          normals: new Float32BufferAttribute(normals, 3),
        }),
        ...(indices?.length && {
          indices: new Uint16BufferAttribute(indices, 1),
        }),
      });
      positions.length = 0;
      normals.length = 0;
      indices.length = 0;
    }
  }

  private addEdgeParseState(
    edgeParseState: EdgeParseState,
    reuseLastPoint?: boolean,
  ): void {
    const { positions, indices } = edgeParseState;
    if (positions.length) {
      //Finalize the edgeGeometry and push it (and start preping next edgeGeometry (positions/indices zeroed))
      this._edgeGeometries.push({
        positions: new Float32BufferAttribute(positions, 3),
        ...(indices?.length && {
          indices: new Uint16BufferAttribute(indices, 1),
        }),
      });
      if (reuseLastPoint) {
        const lastPos = positions.length - 1;
        const lastPoint = [
          positions[lastPos - 2],
          positions[lastPos - 1],
          positions[lastPos],
        ];
        positions.length = 0;
        positions.push(...lastPoint);
        edgeParseState.j = 0;
      } else {
        positions.length = 0;
        edgeParseState.j = -1;
      }
      indices.length = 0;
    }
  }
}
